'use client';

import { createContext, useContext } from 'react';
import { useStore } from 'zustand';

import { useOperationalLevelTwo } from '@/feature-flags/hooks/use-operational-level-two.hook';
import type { UseLevelTwoResult } from '@/feature-flags/types/variant.types';
import { DEFAULT_LEVEL_TWO_CONFIG } from '@/feature-flags/types/variant.types';
import { authStore } from '@/store/auth.store';

const LevelTwoContext = createContext<UseLevelTwoResult>({
  config: DEFAULT_LEVEL_TWO_CONFIG,
  loading: true,
  error: null,
  isFallback: true,
  refetch: async () => {},
  variant: null,
});

interface LevelTwoProviderProps {
  children: React.ReactNode;
  userId?: string;
}

export function LevelTwoProvider({ children, userId: userIdProp }: LevelTwoProviderProps) {
  const userIdFromStore = useStore(authStore, (state) => state.authenticatedUser?.id);

  const userId = userIdProp || userIdFromStore;

  const levelTwoState = useOperationalLevelTwo(userId);

  return (
    <LevelTwoContext.Provider value={levelTwoState}>
      {children}
    </LevelTwoContext.Provider>
  );
}

export function useLevelTwoContext(): UseLevelTwoResult {
  const context = useContext(LevelTwoContext);

  if (!context) {
    throw new Error('useLevelTwoContext must be used within LevelTwoProvider');
  }

  return context;
}
